import { InMemoryLeadsRepository } from "../../../../../test/repositories/inMemoryLeadsRepository"
import { ICreateLeadProtocol } from "../../protocols/leads/ICreateLeadProtocol"
import { CreateLeadUseCase } from "./createLead"
import { InMemoryOriginsRepository } from "../../../../../test/repositories/inMemoryOriginsRepository"
import { makeOrigin } from "../../../../../test/factories/makeOrigin"
import { makeLead } from "../../../../../test/factories/makeLead"
import { OriginStatus, OriginType } from "@/domain/entities/Origin"
import { InMemoryKioskEmailSender } from "test/sender/emails/inMemoryKioskEmailSender"
import { InMemoryTruckEmailSender } from "test/sender/emails/inMemoryTruckEmailSender"
import { Lead } from "@/domain/entities/Lead"

let sut: ICreateLeadProtocol
let repository: InMemoryLeadsRepository
let originsRepository: InMemoryOriginsRepository
let kioskEmailSender: InMemoryKioskEmailSender
let truckEmailSender: InMemoryTruckEmailSender 

describe('Create Lead', () => {
  beforeEach(()=>{
    originsRepository = new InMemoryOriginsRepository()
    repository = new InMemoryLeadsRepository(originsRepository)
    kioskEmailSender = new InMemoryKioskEmailSender()
    truckEmailSender = new InMemoryTruckEmailSender()
    sut = new CreateLeadUseCase(originsRepository, repository, kioskEmailSender, truckEmailSender)
  })

  it('Should be able to create a new lead', async ()=> {
    const origin = makeOrigin({
      name: 'Unit Test'
    })

    await originsRepository.create(origin)

    await sut.execute({
      email: 'test@mail.com',
      name: 'John Doe',
      phone: '11999009900',
      originName: 'Unit Test'
    })

    expect(repository.items.length).toEqual(1)
    expect(repository.items[0].email).toEqual('test@mail.com')
  })

  it('Should be able to create a new lead with a different origin', async ()=> {
    const origin = makeOrigin({
      name: 'Unit Test'
    })
    originsRepository.items.push(origin)

    repository.items.push(Lead.create({
      email: 'test@mail.com',
      name: 'John Doe',
      phone: '11999009900',
      originId: origin.id
    }))

    originsRepository.items.push(makeOrigin({
      name: 'Unit Test 2',
      type: OriginType.TRUCK
    }))

    await sut.execute({
      email: 'test@mail.com',
      name: 'John Doe',
      phone: '11999009900',
      originName: 'Unit Test 2'
    })

    expect(repository.items.length).toEqual(2)
    expect(repository.items[0].email).toEqual('test@mail.com')
    expect(repository.items[1].email).toEqual('test@mail.com')
    expect(repository.items[0].id !== repository.items[1].id).toBeTruthy()
  })

  it('Should be able to send a kiosk email to new lead', async ()=> {
    const origin = makeOrigin({
      name: 'Unit Test'
    })

    await originsRepository.create(origin)

    const {emailStatus} = await sut.execute({
      email: 'test@mail.com',
      name: 'John Doe',
      phone: '11999009900',
      originName: 'Unit Test'
    })

    expect(emailStatus).toEqual('kiosk email sended')
  })

  it('Should be able to send a truck email to new lead', async ()=> {
    const origin = makeOrigin({
      name: 'Unit Test',
      type: OriginType.TRUCK
    })

    await originsRepository.create(origin)

    const {emailStatus} = await sut.execute({
      email: 'test@mail.com',
      name: 'John Doe',
      phone: '11999009900',
      originName: 'Unit Test'
    })

    expect(emailStatus).toEqual('truck email sended')
  })

  it('Should be return an error when kiosk email was not sended', async ()=> {
    const origin = makeOrigin({
      name: 'Unit Test',
    })

    await originsRepository.create(origin)

    kioskEmailSender.status = 'error'


    expect(() => {
      return sut.execute({
        email: 'test@mail.com',
        name: 'John Doe',
        phone: '11999009900',
        originName: 'Unit Test'
      })
    }).rejects.toBeInstanceOf(Error)
  })

  it('Should be return an error when truck email was not sended', async ()=> {
    const origin = makeOrigin({
      name: 'Unit Test',
      type: OriginType.TRUCK
    })

    await originsRepository.create(origin)

    truckEmailSender.status = 'error'


    expect(() => {
      return sut.execute({
        email: 'test@mail.com',
        name: 'John Doe',
        phone: '11999009900',
        originName: 'Unit Test'
      })
    }).rejects.toBeInstanceOf(Error)
  })

  it('Should not be able to create a new lead if origin does not exists', async ()=> {
    expect(()=> {
      return sut.execute({
        email: 'test@mail.com',
        name: 'John Doe',
        originName: 'Unit Test',
        phone: '11999009900',
      })
    }).rejects.toBeInstanceOf(Error)
    expect(repository.items.length).toEqual(0)
  })

  it('Should not be able to create a new lead with same email and same type origin', async ()=> {
    const origin = makeOrigin({
      name: 'Unit Test'
    })
    const leadOnDatabase = makeLead({
      email: 'test@mail.com',
      originId: origin.id,
    })

    await Promise.all([
      originsRepository.create(origin),
      repository.create(leadOnDatabase)
    ])

    expect(()=> {
      return sut.execute({
        email: 'test@mail.com',
        name: 'John Doe',
        originName: 'Unit Test',
        phone: '11999009900',
      })
    }).rejects.toBeInstanceOf(Error)
    expect(repository.items.length).toEqual(1)
  })

  it('Should not be able to create a new lead with same phone and same type origin', async ()=> {
    const origin = makeOrigin({
      name: 'Unit Test'
    })
    const leadOnDatabase = makeLead({
      originId: origin.id,
      phone: '11999009900',
    })

    await Promise.all([
      originsRepository.create(origin),
      repository.create(leadOnDatabase)
    ])

    expect(()=> {
      return sut.execute({
        email: 'test@mail.com',
        name: 'John Doe',
        originName: 'Unit Test',
        phone: '11999009900',
      })
    }).rejects.toBeInstanceOf(Error)
    expect(repository.items.length).toEqual(1)
  })

  it('Should not be able to create a new lead with a invalid email', async ()=> {
    const origin = makeOrigin({
      name: 'Unit Test'
    })

    await Promise.all([
      originsRepository.create(origin)
    ])

    expect(() => {
      return sut.execute({
        email: 'test@.com',
        name: 'John Doe',
        originName: 'Unit Test',
        phone: '11999009900',
      })
    }).rejects.toBeInstanceOf(Error)
    expect(repository.items.length).toEqual(0)
  })

  it('Should not be able to create a new lead with a origin disabled', async ()=> {
    const origin = makeOrigin({
      name: 'Unit Test',
      status: OriginStatus.DISABLED
    })

    await originsRepository.create(origin)

    expect(() => {
      return sut.execute({
        email: 'test@mail.com',
        name: 'John Doe',
        phone: '11999009900',
        originName: 'Unit Test'
      })
    }).rejects.toBeInstanceOf(Error)
  })
})